/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-12 15:26:55
 *
 *******************************************************/
// @flow
import _ from 'lodash';

import React, { useEffect, useState, useMemo } from 'react';
import { CircleTickOutlineMinor } from '@shopify/polaris-icons';
import { Card, Select, Icon, SkeletonDisplayText, SkeletonBodyText } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import ActionChoosePackage from 'src/components/views/subscription-v3/package/action-choose-package';
import { formatNumberInThousand } from 'src/helpers';
import { LARGE_PRODUCT } from 'src/constants/subscription';
import { routes } from 'src/routes';
import { allowToShowBFCM23, BFCM23_CODE } from 'src/helpers/dashboard-helper';
import { connect } from 'react-redux';
import StarsIcon from 'src/assets/images/common/stars-2x.svg';
import { getCorrectPlan } from 'src/helpers/subscription-v3-helper';

//------- Package Card --------
const PackageCardExpireExceed = props => {
  const [advanceList, setAdvanceList] = useState([]);
  const [packageList, setPackageList] = useState([]);
  const [hasPackageRequest, setHasPackageRequest] = useState(false);

  const {
    i18n,
    packageInfo = {},
    index,
    invoice,
    confirmationUrl,
    currentTotalProducts,
    gettingInvoice,
    gettingPackage,
    packagePlans = [],
    history,
    packageSelected,
    setPackageSelected,
    planNumberRequest,
    tenantApp,
    correctPlan,
    packageName,
    isStoreExpired,
    allPackagePlans
  } = props;
  const correctPlanName = _.get(correctPlan, 'name', '');
  const isRecommended = index === 2 && correctPlanName.includes('essential');
  const isMostPopular = index === 1 && !correctPlanName.includes('professional');
  const isCurrentPlan = correctPlanName.includes(packageName);
  const isHighlightUI = useMemo(() => {
    if (packageName === 'professional') {
      return true;
    }
    if (isCurrentPlan) {
      return false;
    }
    if (correctPlanName.includes('basic') && index > 0) {
      return true;
    }
    if (correctPlanName.includes('essential') && index > 1) {
      return true;
    }
    return false;
  }, [packageName, correctPlanName, tenantApp]);

  const bfcm23 = useMemo(() => {
    return allowToShowBFCM23(tenantApp);
  }, [tenantApp]);

  const { title, advanceLabel, name, subtext } = packageInfo;

  const isDisabled =
    packageSelected !== '50,000+' && currentTotalProducts > parseInt(packageSelected);

  useEffect(() => {
    setAdvanceList(_.get(packageInfo, 'advanceList', []));

    // Init value package from package list and invoice
    // Check if has request large plan
    if (planNumberRequest) {
      // Get package from router request large plan
      const numberOfProductsRequest = parseFloat(planNumberRequest);
      const numberProductsRequest =
        numberOfProductsRequest >= 1000
          ? numberOfProductsRequest * 1000 * 3
          : numberOfProductsRequest * 1000;

      if (!_.isNaN(numberOfProductsRequest) && numberProductsRequest >= currentTotalProducts) {
        const findPackage = packagePlans.find(e => e.products === numberProductsRequest);
        if (findPackage) {
          setPackageSelected(`${findPackage.products}`);
          setHasPackageRequest(true);
          setPackageList(packagePlans);
        } else {
          // Not correct plan for store => go to package page
          handleBackPackagePage();
        }
      } else {
        handleBackPackagePage();
      }
      return;
    }
    // Don't have request large plan => init value package list to view
    const currentPlanProducts = _.get(invoice, 'chargeInfo.products', 0);
    const packages = packagePlans.filter(e => e.products <= LARGE_PRODUCT);
    // Ensure option request large plan
    const correctProducts =
      currentTotalProducts > currentPlanProducts ? currentTotalProducts : currentPlanProducts;
    packages.push({
      name: 'request_large_plan',
      products: '50,000+',
      price: packagePlans.find(e =>
        correctProducts > LARGE_PRODUCT ? e.products >= correctProducts : e.products > LARGE_PRODUCT
      )?.price
    });

    setPackageList(packages);

    const correctPackage = packages.find(e =>
      currentTotalProducts > LARGE_PRODUCT
        ? e.name === 'request_large_plan'
        : currentPlanProducts > currentTotalProducts && currentPlanProducts <= LARGE_PRODUCT
        ? e.products === currentPlanProducts
        : e.products > currentTotalProducts
    );
    setPackageSelected(`${correctPackage?.products}`);
  }, [packageInfo, packagePlans, planNumberRequest, currentTotalProducts]);

  // Redirect to shopify confirmation page
  useEffect(() => {
    if (confirmationUrl) {
      window.top.location.href = confirmationUrl;
      return;
    }
  }, [confirmationUrl]);

  // Function
  // Handle back to subscription page
  const handleBackPackagePage = () => {
    history.push(routes.subscription.plan());
  };

  // Init list of product count
  const options = packageList.map(item => ({
    value: `${item.products}`,
    name: item.name,
    label:
      item.name === 'request_large_plan' ? item.products : formatNumberInThousand(item.products)
  }));

  const getTag = ({ isRecommended, isCurrentPlan, isMostPopular }) => {
    if (isCurrentPlan) return <div className="package-tag current-plan">Current plan</div>;
    if (isRecommended) return <div className="package-tag recommended">Recommended</div>;
    if (isMostPopular)
      return (
        <div className="package-tag most-popular">
          {i18n.translate('AdminHTML.SubscriptionV3.Packages.mostPopular')}
        </div>
      );
    return <div></div>;
  };

  return (
    <>
      <Card>
        {getTag({
          isRecommended,
          isCurrentPlan,
          isMostPopular
        })}
        <div className="package-name">{i18n.translate(title)}</div>
        <div>
          {subtext
            ? i18n.translate(subtext)
            : i18n.translate('AdminHTML.SubscriptionV3.Packages.startAt')}
        </div>
        {gettingInvoice || gettingPackage ? (
          <SkeletonDisplayText size="small" />
        ) : (
          <div className="price">
            ${packageList.find(e => `${e.products}` === packageSelected)?.price}
            <span>{i18n.translate('AdminHTML.SubscriptionV3.Common.perMo')}</span>
          </div>
        )}
        {gettingInvoice || gettingPackage || !packageSelected || packageSelected === 'undefined' ? (
          <SkeletonBodyText />
        ) : (
          <>
            <div>
              <Select
                options={options}
                disabled={hasPackageRequest}
                value={packageSelected}
                onChange={e => setPackageSelected(e)}
              />
              <p>{i18n.translate('AdminHTML.SubscriptionV3.Common.products')}</p>
            </div>
            <div className="package-action">
              <ActionChoosePackage
                packageInfo={packageInfo}
                primary={isHighlightUI}
                packagePlans={packagePlans}
                disabled={isDisabled}
                correctPlan={packageList.find(e => `${e.products}` === packageSelected)}
                isForExpireExceed={true}
                isCurrentPlan={isCurrentPlan}
                isStoreExpired={isStoreExpired}
                products={packageSelected}
                allPackagePlans={allPackagePlans}
                packageSelected={packageSelected}
              />
            </div>
          </>
        )}
        <div className="question-title">{i18n.translate(advanceLabel)}</div>
        <div className="advances-package">
          {advanceList.map((e, index) => (
            <div key={`${name}-advanceName-${index}`} className="advance-line">
              <Icon source={CircleTickOutlineMinor} /> <span>{i18n.translate(e)}</span>
            </div>
          ))}
        </div>
        {bfcm23.allow && (
          <div className="bfcm23-package">
            {bfcm23.packagesHighlight?.includes(name) && (
              <div className="text-discount">
                <img src={StarsIcon} alt="stars" style={{ marginRight: '4px' }} />
                <span>
                  {i18n.translate('AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Package_text', {
                    code: <b style={{ color: 'rgba(124, 255, 179, 1)' }}>{BFCM23_CODE.PLAN}</b>
                  })}
                </span>
              </div>
            )}
          </div>
        )}
        {isHighlightUI && <span className="highlight" style={{ display: 'none' }}></span>}
      </Card>
    </>
  );
};

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});
  const chargeInfo = _.get(state.subscriptionV3, 'invoice.chargeInfo', {});
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts');
  // Get correct plan
  const correctPlan = getCorrectPlan(chargeInfo, packagePlans, currentTotalProducts);
  return {
    tenantApp,
    correctPlan
  };
};

export default withI18n()(connect(mapStateToProps, null)(PackageCardExpireExceed));
